import { t } from 'i18n';
import React, { useState } from 'react';
import { Col, Modal, Row, styled, Button } from '@teko/ds-desktop';
import { CartPromotionGroup, PromotionGroupType } from '@teko/cart/v2';
import { RightOutlined } from '@teko/ds-icon';
import { BlockPromotion, PromotionItem, PromotionStatus } from '@teko/ui-cart';
import { flattenPromotionsFromPromotionGroups } from '../utils';
import { useCartV2 } from 'contexts';
import { getTrackingData } from '@teko/tracking';

const ModalContent = styled.div`
  height: 320px;
  overflow-y: auto;
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar {
    width: 6px;
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #999;
  }
`;

const AppliedPromotionCol = styled(Col)`
  padding: 8px 0;
  margin: 12px 0;
  background-color: #f3f3f7;
  border-radius: 4px;
`;

const StyledRightArrowCol = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ProductPromotion = ({
  productPromotions,
  productId,
  isViewOnly = false,
  productSku,
  comboPromotion,
}: {
  productPromotions: CartPromotionGroup[];
  productId: string;
  isViewOnly?: boolean;
  productSku?: string;
  comboPromotion?: number;
}) => {
  const { onApplyPromotionInCartPage, onRemovePromotion, cart } = useCartV2();

  const [showModal, setShowModal] = useState(false);

  // all promotions of an item (not being grouped)
  const allItemPromotions =
    flattenPromotionsFromPromotionGroups(productPromotions);

  // filter all promotions with cartPromotionId is not null
  const appliedPromotions =
    allItemPromotions?.filter(({ cartPromotionId }) => !!cartPromotionId) || [];

  // only show select promotion button when productPromotions have cartPromotionId = null and has at least 1 promotion with isSelectable = true
  const showSelectPromotionButton =
    allItemPromotions?.every((promotion) => !promotion.cartPromotionId) &&
    allItemPromotions?.some((promotion) => !!promotion.isSelectable);

  if (!!appliedPromotions.length || showSelectPromotionButton) {
    return (
      <>
        {!!appliedPromotions.length && (
          <Row justify="start" wrap={false}>
            {!isViewOnly && <Col flex="4%" />}
            <AppliedPromotionCol
              flex={isViewOnly ? '100%' : '96%'}
              className="applied-promotion"
            >
              <Row>
                <Col flex="94%">
                  {appliedPromotions.map((promotion, index) => (
                    <PromotionItem
                      key={`${promotion.promotionId}-${index}`}
                      data={{
                        ...promotion,
                        promotionStatus: PromotionStatus.FIXED,
                      }}
                      marginLeft="s16"
                      marginRight="s16"
                      marginBottom="s4"
                      marginTop="s4"
                    />
                  ))}
                </Col>
                <StyledRightArrowCol flex="6%">
                  <RightOutlined
                    size={24}
                    color="placeholder"
                    onClick={() => {
                      setShowModal(true);
                    }}
                    {...getTrackingData({
                      isTrackContent: true,
                      contentName: 'viewPromotionIcon',
                      regionName: 'openPromotionPopup',
                      target: 'cartProductPromotion',
                      payload: JSON.stringify({
                        screenName: 'shoppingCart',
                        promotionIds: appliedPromotions.map(
                          (promotion) => promotion.promotionId,
                        ),
                        sku: productSku,
                        cartId: cart.id,
                      }),
                    })}
                  />
                </StyledRightArrowCol>
              </Row>
            </AppliedPromotionCol>
          </Row>
        )}
        {showSelectPromotionButton && (
          <ProductPromotionButton
            onClick={() => setShowModal(true)}
            isViewOnly={isViewOnly}
          />
        )}
        <Modal
          visible={showModal}
          onClose={() => setShowModal(false)}
          onHide={() => setShowModal(false)}
          okText=""
          cancelText={t('Đóng')}
          width={540}
          title={t('Khuyến mãi sản phẩm')}
          showCloseIcon={false}
          onCancel={() => setShowModal(false)}
        >
          <ModalContent>
            {productPromotions.map((blockPromotion) => {
              const {
                title,
                description,
                promotions = [],
                groupType,
              } = blockPromotion;
              const isAppliedPromotionBlock = groupType
                ? groupType === PromotionGroupType.GROUP_TYPE_DEFAULT
                : promotions.every((value) => !value.isSelectable);
              return (
                //only show promotions group includes at least a promotion has title not null
                promotions.find((promotion) => !!promotion.title) && (
                  <BlockPromotion
                    key={title}
                    title={title?.toLocaleUpperCase()}
                    description={description?.toLocaleUpperCase()}
                    promotions={promotions}
                    styledTitle={
                      !isAppliedPromotionBlock
                        ? {
                            marginLeft: 's16',
                            marginBottom: 's4',
                            marginTop: 's12',
                          }
                        : {}
                    }
                    styledPromotionItem={
                      isAppliedPromotionBlock
                        ? { marginLeft: 's4', marginBottom: 's8' }
                        : {
                            marginLeft: 's16',
                            marginBottom: 's12',
                            marginRight: 's16',
                          }
                    }
                    backgroundColor={
                      isAppliedPromotionBlock ? 'white' : 'background'
                    }
                    marginBottom="s12"
                    paddingBottom="s12"
                    onApplyPromotion={onApplyPromotionInCartPage}
                    onRemovePromotion={onRemovePromotion}
                    productId={productId}
                    productSku={productSku}
                    comboPromotion={comboPromotion}
                    // only load more in selection block promotion
                    showMore={!isAppliedPromotionBlock ? 2 : undefined}
                    groupType={groupType}
                  />
                )
              );
            })}
          </ModalContent>
        </Modal>
      </>
    );
  }
  return null;
};

const ProductPromotionButton = ({
  onClick,
  isViewOnly = false,
}: {
  onClick: () => void;
  isViewOnly?: boolean;
}) => (
  <Row justify="start" wrap={false}>
    {!isViewOnly && <Col flex="4%" />}
    <Col flex={isViewOnly ? '100%' : '96%'}>
      <StyledChangePromotion onClick={onClick}>
        {t('Chọn khuyến mãi')}&nbsp;
        <RightOutlined size={13} color="link500" />
      </StyledChangePromotion>
    </Col>
  </Row>
);

const StyledChangePromotion = styled(Button)`
  color: #1990ff;
  overflow: unset;
  border: none;
  height: 20px;
  min-width: 58px;
  margin-top: 7px;
  font-size: 13px;
  padding: 0;
`;
